import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs';
import {
    IUserResponse,
    ICreateSystemAdminDto,
    ICreateAdminDto,
    ICreateHRDto,
    ICreateSupervisorDto,
    ICreateGuardDto,
    ICreatePostSupervisorDto,
    ICreatePostResidentDto,
    IUpdateUserDto,
    IUserFilters,
    CreateUserDto,
    Roles,
} from '../../interfaces';
import { USER_ENDPOINTS, USER_CACHE_KEYS } from '../../constants';
import { UniversalService, UniversalServiceConfig } from './universal.service';
import { RolesService } from '../roles.service';

/**
 * Configuração do UniversalService para Shifts
 * Seguindo exatamente a mesma lógica do ShiftsController do backend
 */
const USER_CONFIG: UniversalServiceConfig<IUserResponse, CreateUserDto, IUpdateUserDto, IUserFilters> = {
    entityName: 'users',
    endpoints: USER_ENDPOINTS,
    cacheKeys: USER_CACHE_KEYS,
    defaultCacheTtl: 30000, // 30 segundos
    checkPermissions: (dependencies: { rolesService: RolesService }) => {
        return dependencies.rolesService.hasAnyRole([Roles.SYSTEM_ADMIN, Roles.ADMIN, Roles.GUARD, Roles.DOORMAN, Roles.SUPERVISOR, Roles.HR, Roles.POST_SUPERVISOR, Roles.POST_RESIDENT]);
    },
};

/**
 * Serviço para gerenciamento de usuários
 * Seguindo as regras do projeto: métodos em português, signals do Angular
 * Migrado para usar ApiService centralizado com cache e loading states
 */
@Injectable({ providedIn: 'root' })
export class UserService extends UniversalService<IUserResponse, CreateUserDto, IUpdateUserDto, IUserFilters> {
    private readonly rolesService = inject(RolesService);

    constructor() {
        super(USER_CONFIG);
    }

    protected override getPermissionsDependencies(): { rolesService: RolesService } {
        return { rolesService: this.rolesService };
    }
    // Estado reativo com signals
    private readonly usersCache = signal<IUserResponse[]>([]);
    private readonly currentUser = signal<IUserResponse | null>(null);
    private readonly usersEmTurnoNoPostoCache = signal<IUserResponse[]>([]);

    // Computed signals públicos
    readonly usersListCache = computed(() => this.usersCache());
    readonly getCurrentUser = computed(() => this.currentUser());
    readonly getUsersEmTurnoNoPosto = computed(() => this.usersEmTurnoNoPostoCache());

    /**
     * Busca usuários com filtro de pesquisa
     * @param query Termo de pesquisa
     * @param filters Filtros adicionais
     * @returns Observable com usuários paginados
     */
    buscarUsuarios(query: string, filters: any = {}): Observable<any> {
        // Verificar permissões se configurado
        if (this.config.checkPermissions && !this.config.checkPermissions(this.getPermissionsDependencies())) {
            return throwError(() => new Error(`Acesso negado: permissão insuficiente para visualizar ${this.config.entityName}`));
        }

        const params = {
            q: query,
            page: filters.page || 1,
            limit: filters.limit || 20,
            orderBy: filters.orderBy || 'name',
            orderDirection: filters.orderDirection || 'asc',
            ...filters,
        };

        return this.apiService.get<any>(`${this.config.endpoints.BASE}/search`, { params }).pipe(
            tap((response: any) => {
                // Atualizar cache com resultados da busca
                if (response.data) {
                    this.usersCache.set(response.data);
                }
            }),
            catchError(error => {
                console.error(`Erro ao buscar usuários:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Lista funcionários CLT da empresa (exclui POST_SUPERVISOR/POST_RESIDENT).
     * NÃO escreve em usersCache — esse cache é semântico da lista geral.
     * @param filters Filtros de paginação/ordenação
     */
    buscarEmployees(filters: any = {}): Observable<any> {
        if (this.config.checkPermissions && !this.config.checkPermissions(this.getPermissionsDependencies())) {
            return throwError(() => new Error(`Acesso negado: permissão insuficiente para visualizar funcionários`));
        }

        const params = {
            page: filters.page || 1,
            limit: filters.limit || 20,
            orderBy: filters.orderBy || 'name',
            orderDirection: filters.orderDirection || 'asc',
        };

        return this.apiService.get<any>(USER_ENDPOINTS.EMPLOYEES, {
            params,
            cacheKey: USER_CACHE_KEYS.EMPLOYEES_LIST(JSON.stringify(params)),
        }).pipe(
            catchError(error => {
                console.error(`Erro ao buscar funcionários:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Busca funcionários CLT por nome/email/telefone.
     * NÃO escreve em usersCache — mesmo motivo do buscarEmployees.
     * @param query Termo de pesquisa
     * @param filters Filtros de paginação/ordenação
     */
    buscarEmployeesComBusca(query: string, filters: any = {}): Observable<any> {
        if (this.config.checkPermissions && !this.config.checkPermissions(this.getPermissionsDependencies())) {
            return throwError(() => new Error(`Acesso negado: permissão insuficiente para buscar funcionários`));
        }

        const params = {
            q: query,
            page: filters.page || 1,
            limit: filters.limit || 20,
            orderBy: filters.orderBy || 'name',
            orderDirection: filters.orderDirection || 'asc',
        };

        return this.apiService.get<any>(USER_ENDPOINTS.EMPLOYEES_SEARCH, {
            params,
            cacheKey: USER_CACHE_KEYS.EMPLOYEES_SEARCH(JSON.stringify(params)),
        }).pipe(
            catchError(error => {
                console.error(`Erro ao buscar funcionários (search):`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Lista TODOS os funcionários CLT (sem paginação, sem busca).
     * Usado por dropdowns de formulários HR (vacation, termination, etc).
     * Retorna array simples, NÃO objeto paginado.
     * NÃO escreve em usersCache.
     */
    buscarEmployeesAll(): Observable<any[]> {
        if (this.config.checkPermissions && !this.config.checkPermissions(this.getPermissionsDependencies())) {
            return throwError(() => new Error(`Acesso negado: permissão insuficiente para listar funcionários`));
        }

        return this.apiService.get<any[]>(USER_ENDPOINTS.EMPLOYEES_ALL, {
            cacheKey: USER_CACHE_KEYS.EMPLOYEES_ALL,
        }).pipe(
            catchError(error => {
                console.error(`Erro ao listar funcionários:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Lista usuários com turno ativo no posto (GUARD/SUPERVISOR/DOORMAN).
     * Endpoint novo sem CASL. Substitui /users/active-guards-on-shift-post/:postId
     * (que tinha filtro CASL quebrando para GUARD logado).
     * NÃO escreve em usersCache.
     */
    buscarGuardsEmTurno(postId: string): Observable<any[]> {
        if (!postId) {
            return throwError(() => new Error('postId é obrigatório'));
        }

        return this.apiService.get<any[]>(USER_ENDPOINTS.GUARDS_ON_SHIFT, {
            params: { postId },
            cacheKey: USER_CACHE_KEYS.GUARDS_ON_SHIFT(postId),
        }).pipe(
            catchError(error => {
                console.error(`Erro ao listar vigilantes em turno:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Busca clientes (POST_SUPERVISOR e POST_RESIDENT) de um posto específico
     * @param postId ID do posto
     * @param filters Filtros de paginação
     * @returns Observable com clientes paginados
     */
    buscarClientesPorPosto(postId: string, filters: any = {}): Observable<any> {
        // Verificar permissões
        if (this.config.checkPermissions && !this.config.checkPermissions(this.getPermissionsDependencies())) {
            return throwError(() => new Error(`Acesso negado: permissão insuficiente para visualizar ${this.config.entityName}`));
        }

        const params = {
            page: filters.page || 1,
            limit: filters.limit || 20,
            orderBy: filters.orderBy || 'name',
            orderDirection: filters.orderDirection || 'asc',
        };

        return this.apiService.get<any>(`${this.config.endpoints.BASE}/post/${postId}/clients`, { params }).pipe(
            tap((response: any) => {
                // Atualizar cache com resultados
                if (response.data) {
                    this.usersCache.set(response.data);
                }
            }),
            catchError(error => {
                console.error(`Erro ao buscar clientes do posto:`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Busca todos os itens sem paginação (GET /all)
     * @returns Observable com todos os itens
     */
    buscarVigilantesAtivosEmTurnoNoPosto(postId: string): Observable<IUserResponse[]> {
        // Verificar permissões específicas para este endpoint (liberado para mais roles)
        const hasPermission = this.rolesService.hasAnyRole([
            Roles.SYSTEM_ADMIN,
            Roles.ADMIN,
            Roles.HR,
            Roles.SUPERVISOR,
            Roles.GUARD,
            Roles.DOORMAN,
            Roles.JARDINER,
            Roles.MAINTENANCE_ASSISTANT,
            Roles.MONITORING_OPERATOR,
            Roles.ADMINISTRATIVE_ASSISTANT,
            Roles.POST_SUPERVISOR,
        ]);

        if (!hasPermission) {
            return throwError(() => new Error(`Acesso negado: permissão insuficiente para visualizar vigilantes em turno`));
        }

        return this.apiService
            .get<IUserResponse[]>(`${this.config.endpoints['GUARD_POST_BY_ID'](postId)}`, {
                useCache: false,
                cacheKey: this.config.cacheKeys['GUARD_POST_BY_ID'](postId),
                cacheTtl: this.config.defaultCacheTtl || 300000,
            })
            .pipe(
                tap(items => {
                    if (items && items.length > 0) {
                        const transformedData = this.config.transformData ? items.map(item => this.config.transformData!(item)) : items;
                        this.usersEmTurnoNoPostoCache.set(transformedData);
                    }

                    this.clearError();
                }),
                catchError(error => {
                    console.error(`Erro ao buscar todos os ${this.config.entityName}:`, error);
                    this.setError(`Erro ao buscar todos os ${this.config.entityName}`);
                    return throwError(() => error);
                })
            );
    }
    /**
     * Cria novo System Admin
     * @param dto Dados do System Admin
     * @returns Observable com usuário criado
     */
    criarNovoSystemAdmin(dto: ICreateSystemAdminDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.SYSTEM_ADMIN, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo Admin
     * @param dto Dados do Admin
     * @returns Observable com usuário criado
     */
    criarNovoAdmin(dto: ICreateAdminDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.ADMIN, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo HR
     * @param dto Dados do HR
     * @returns Observable com usuário criado
     */
    criarNovoHR(dto: ICreateHRDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.HR, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo Supervisor
     * @param dto Dados do Supervisor
     * @returns Observable com usuário criado
     */
    criarNovoSupervisor(dto: ICreateSupervisorDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.SUPERVISOR, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo Guard
     * @param dto Dados do Guard
     * @returns Observable com usuário criado
     */
    criarNovoGuard(dto: ICreateGuardDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.GUARD, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo Post Supervisor
     * @param dto Dados do Post Supervisor
     * @returns Observable com usuário criado
     */
    criarNovoPostSupervisor(dto: ICreatePostSupervisorDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.POST_SUPERVISOR, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo Post Resident
     * @param dto Dados do Post Resident
     * @returns Observable com usuário criado
     */
    criarNovoPostResident(dto: ICreatePostResidentDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.POST_RESIDENT, dto, {
                useCache: false,
            })
            .pipe(
                tap(() => {
                    // Invalidar cache de usuários
                    this.apiService.clearCacheByPattern(USER_CACHE_KEYS.PATTERN);
                })
            );
    }

    /**
     * Cria novo Post Supervisor via registro público (sem autenticação)
     * @param dto Dados do Post Supervisor
     * @returns Observable com usuário criado
     */
    criarPostSupervisorPublico(dto: ICreatePostSupervisorDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.POST_SUPERVISOR_PUBLIC, dto, {
                useCache: false,
            });
    }

    /**
     * Cria novo Post Resident via registro público (sem autenticação)
     * @param dto Dados do Post Resident
     * @returns Observable com usuário criado
     */
    criarPostResidentPublico(dto: ICreatePostResidentDto): Observable<IUserResponse> {
        return this.apiService
            .post<IUserResponse>(USER_ENDPOINTS.POST_RESIDENT_PUBLIC, dto, {
                useCache: false,
            });
    }

    /**
     * Método genérico para criar usuário baseado no role
     * @param dto Dados do usuário
     * @returns Observable com usuário criado
     */
    override criar(dto: CreateUserDto & { role: Roles }): Observable<IUserResponse> {
        switch (dto.role) {
            case Roles.SYSTEM_ADMIN:
                return this.criarNovoSystemAdmin(dto as ICreateSystemAdminDto);
            case Roles.ADMIN:
                return this.criarNovoAdmin(dto as ICreateAdminDto);
            case Roles.HR:
                return this.criarNovoHR(dto as ICreateHRDto);
            case Roles.SUPERVISOR:
                return this.criarNovoSupervisor(dto as ICreateSupervisorDto);
            case Roles.GUARD:
                return this.criarNovoGuard(dto as ICreateGuardDto);
            case Roles.POST_SUPERVISOR:
                return this.criarNovoPostSupervisor(dto as ICreatePostSupervisorDto);
            case Roles.POST_RESIDENT:
                return this.criarNovoPostResident(dto as ICreatePostResidentDto);
            default:
                return super.criar(dto as CreateUserDto & { role: Roles });
        }
    }
}
